import { styled } from "goober";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { zIndexes } from "../lib/zIndexes";
import { Icon } from "./Icon";
import { MenuItem, MenuItemLink } from "./MenuItem";
import { Text } from "./Text";

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

type Props = {
  // Distance from the viewport's right edge to align the menu's right edge to,
  // and the top to drop from — i.e. anchored under the right-clicked connector.
  right: number;
  top: number;
  editTo: string;
  parentNoun: string;
  onRemove: () => void;
  onClose: () => void;
};

export function ConnectorAvatarContextMenu({
  right,
  top,
  editTo,
  parentNoun,
  onRemove,
  onClose,
}: Props) {
  const [containerRef, menuRef] = useMouseDownOutside(onClose);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Keep the menu on-screen when the connector sits near the left edge
  // (its min-width is 180).
  const clampedRight = Math.max(0, Math.min(right, window.innerWidth - 188));

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu
        ref={menuRef}
        data-popover
        data-direction="down"
        style={{ right: clampedRight, top }}
      >
        <MenuItemLink to={editTo} onClick={onClose}>
          <Icon name="settings-2" size={16} />
          <Text variant="text-regular">Manage connector</Text>
        </MenuItemLink>
        <MenuItem
          onClick={() => {
            onRemove();
            onClose();
          }}
        >
          <Icon name="circle-minus" size={16} />
          <Text variant="text-regular">{`Remove from ${parentNoun}`}</Text>
        </MenuItem>
      </Menu>
    </div>,
    document.body
  );
}
